import React, { memo, useCallback, useMemo } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  Button,
  HStack,
  Text,
} from "@chakra-ui/react";

import { useDistributionGroups } from "modules/providers/distribution-groups-provider";
import colors from "app/colors";
import { useAuth } from "modules/providers/auth-provider";
import { useMutation } from "@apollo/client";
import { useMixpanel } from "modules/providers/mixpanel-provider";
import { MIXPANEL_EVENT_TYPE } from "modules/providers/mixpanel-provider/mixpanel-provider";
import ExpandCollapseAccordionButton from "./expand-collapse-accordion-button";
import useFilterForSelectedState from "./use-filter-for-selected-state";
import {
  getDistributionGroupMention,
  sortDistributionGroups,
} from "modules/utils/Functions";
import {
  EditDistributionGroupSeenMutation,
  EditDistributionGroupSeenMutationVariables,
  DistributionGroupFragment,
  SearchFilterType,
} from "gql/graphql";
import EDIT_DISTRIBUTION_GROUP_SEEN_MUTATION from "modules/contacts/networking/edit-distribution-group-seen";
import { AtSignIcon, GroupsOutlineIcon } from "modules/icons";
import useSearchFilter from "modules/hooks/use-search-filter";
import { TAG_GROUP_THREADS_PATH } from "modules/utils/Constants";
import useAppState from "modules/hooks/use-app-state";
import selectedTagGroupNavLinkProps from "./selected-tag-group-nav-link-props";
import Link from "modules/layouts/_shared/link";

const unseenColor = colors.whiteTertiary;
const seenColor = colors.gray30;

const LeftNavDistributionGroups = () => {
  const { distributionGroups } = useDistributionGroups();
  const { me } = useAuth();
  const filterForSelectedState = useFilterForSelectedState();
  const { track } = useMixpanel();
  const { getUrl } = useSearchFilter();

  const { booleanValue: isExpanded, updateBoolean: setIsExpanded } =
    useAppState({
      appStateType: "isDistributionGroupsExpanded",
    });

  const {
    booleanValue: isInactiveExpanded,
    updateBoolean: setIsInactiveExpanded,
  } = useAppState({
    appStateType: "isInactiveDistributionGroupsExpanded",
  });

  const toggleExpand = useCallback(
    (expandOnly: boolean) => () =>
      setIsExpanded(expandOnly ? true : !isExpanded),
    [isExpanded, setIsExpanded],
  );

  const toggleInactiveExpand = useCallback(
    (expandOnly: boolean) => () =>
      setIsInactiveExpanded(expandOnly ? true : !isInactiveExpanded),
    [isInactiveExpanded, setIsInactiveExpanded],
  );

  const [editDistributionGroupSeenAPI] = useMutation<
    EditDistributionGroupSeenMutation,
    EditDistributionGroupSeenMutationVariables
  >(EDIT_DISTRIBUTION_GROUP_SEEN_MUTATION);

  const activeUserDistributionGroups = useMemo(() => {
    if (!me) {
      return [];
    }
    return distributionGroups
      .filter(
        (distributionGroup) =>
          distributionGroup.userIdList.includes(me.id) &&
          distributionGroup.active,
      )
      .sort(sortDistributionGroups);
  }, [distributionGroups, me]);

  const inactiveUserDistributionGroups = useMemo(() => {
    if (!me) {
      return [];
    }
    return distributionGroups
      .filter(
        (distributionGroup) =>
          distributionGroup.userIdList.includes(me.id) &&
          !distributionGroup.active,
      )
      .sort(sortDistributionGroups);
  }, [distributionGroups, me]);

  const onDistributionGroupClicked = useCallback(
    (distributionGroup: DistributionGroupFragment) => () => {
      if (distributionGroup.isUnseen) {
        editDistributionGroupSeenAPI({
          variables: {
            id: distributionGroup.id,
            isSeen: true,
          },
          optimisticResponse: {
            editDistributionGroup: {
              __typename: "DistributionGroup",
              id: distributionGroup.id,
              isUnseen: false,
            },
          },
        });
      }
      track(MIXPANEL_EVENT_TYPE.LINK, {
        "Link Location": "Left Hand Nav",
        "Link Action": "Group",
        "Link Name": getDistributionGroupMention(distributionGroup),
      });
    },
    [editDistributionGroupSeenAPI, track],
  );

  if (!me) return null;

  return (
    <Accordion allowMultiple index={isExpanded ? [0] : []} w="full">
      <AccordionItem border="none">
        {({ isExpanded }) => (
          <>
            <h1>
              <HStack spacing={0} py={1}>
                <ExpandCollapseAccordionButton
                  isExpanded={isExpanded}
                  canExpand
                  onClick={toggleExpand(false)}
                />
                <Button variant="link" ml={-1} onClick={toggleExpand(false)}>
                  <HStack>
                    <GroupsOutlineIcon fill={colors.orangePrimary} />
                    <Text flexShrink={1} fontSize={14} color={colors.gray30}>
                      My Groups
                    </Text>
                  </HStack>
                </Button>
              </HStack>
            </h1>
            <AccordionPanel py={0} pl={0} pr={0}>
              <Accordion allowMultiple index={[]}>
                {activeUserDistributionGroups.map((distributionGroup) => (
                  <AccordionItem key={distributionGroup.id} border="none">
                    {({ isExpanded }) => (
                      <h1>
                        <HStack spacing={1}>
                          <ExpandCollapseAccordionButton
                            isExpanded={isExpanded}
                            canExpand={false}
                          />
                          <Link
                            href={getUrl(TAG_GROUP_THREADS_PATH, undefined, {
                              type: SearchFilterType.DistributionGroupIdList,
                              objectIdListValue: [distributionGroup.id],
                            })}
                            onClick={onDistributionGroupClicked(
                              distributionGroup,
                            )}
                            fontSize="sm"
                            w="full"
                            py={0.5}
                            {...(filterForSelectedState?.id ===
                              distributionGroup.id &&
                              selectedTagGroupNavLinkProps)}
                          >
                            <HStack spacing={1} alignItems="flex-start">
                              <AtSignIcon
                                mt="2.5px"
                                fill={
                                  distributionGroup.isUnseen
                                    ? unseenColor
                                    : seenColor
                                }
                              />
                              <Text
                                overflow="hidden"
                                color={
                                  distributionGroup.isUnseen
                                    ? unseenColor
                                    : seenColor
                                }
                                fontSize={14}
                                fontWeight={
                                  distributionGroup.isUnseen ? 750 : 400
                                }
                              >
                                {distributionGroup.name}
                              </Text>
                            </HStack>
                          </Link>
                        </HStack>
                      </h1>
                    )}
                  </AccordionItem>
                ))}
                {inactiveUserDistributionGroups.length > 0 && (
                  <AccordionPanel py={0} pl={5} pr={0}>
                    <Accordion
                      allowMultiple
                      index={isInactiveExpanded ? [0] : []}
                    >
                      <AccordionItem border="none">
                        <HStack spacing={1} py={1}>
                          <ExpandCollapseAccordionButton
                            isExpanded={isInactiveExpanded}
                            canExpand
                            onClick={toggleInactiveExpand(false)}
                          />
                          <Button
                            variant="link"
                            onClick={toggleInactiveExpand(true)}
                          >
                            <HStack spacing={1}>
                              <Text
                                flexShrink={1}
                                color={colors.grayMain}
                                fontSize={14}
                                fontStyle="italic"
                              >
                                Archived Groups
                              </Text>
                            </HStack>
                          </Button>
                        </HStack>
                        <AccordionPanel py={0} pl={0} pr={0}>
                          <Accordion allowMultiple index={[]}>
                            {inactiveUserDistributionGroups.map(
                              (distributionGroup) => (
                                <AccordionItem
                                  key={distributionGroup.id}
                                  border="none"
                                >
                                  {({ isExpanded }) => (
                                    <h1>
                                      <HStack spacing={1}>
                                        <ExpandCollapseAccordionButton
                                          isExpanded={isExpanded}
                                          canExpand={false}
                                        />
                                        <Link
                                          href={getUrl(
                                            TAG_GROUP_THREADS_PATH,
                                            undefined,
                                            {
                                              type: SearchFilterType.DistributionGroupIdList,
                                              objectIdListValue: [
                                                distributionGroup.id,
                                              ],
                                            },
                                          )}
                                          onClick={onDistributionGroupClicked(
                                            distributionGroup,
                                          )}
                                          fontSize="sm"
                                          w="full"
                                          py={0.5}
                                          {...(filterForSelectedState?.id ===
                                            distributionGroup.id &&
                                            selectedTagGroupNavLinkProps)}
                                        >
                                          <HStack
                                            spacing={1}
                                            alignItems="flex-start"
                                          >
                                            <AtSignIcon
                                              mt="2.5px"
                                              fill={colors.grayMain}
                                            />
                                            <Text
                                              overflow="hidden"
                                              color={colors.grayMain}
                                              fontSize={14}
                                              fontWeight={400}
                                              fontStyle="italic"
                                            >
                                              {distributionGroup.name}
                                            </Text>
                                          </HStack>
                                        </Link>
                                      </HStack>
                                    </h1>
                                  )}
                                </AccordionItem>
                              ),
                            )}
                          </Accordion>
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  </AccordionPanel>
                )}
              </Accordion>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  );
};

export default memo(LeftNavDistributionGroups);
